/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

import { isNotEmpty, IsNotEmptyPipe, IsNotNullPipe } from '@common/tech-shared';
import { IconComponent } from '../icon/icon.component';
import { FileIconComponent, FileiconVariants } from '../icons/file-icon/file-icon.component';
import { SpinnerIconComponent } from '../icons/spinner-icon/spinner-icon.component';
import { AttachmentBadge } from './attachment.model';

@Component({
  selector: 'ods-attachment',
  imports: [CommonModule, SpinnerIconComponent, FileIconComponent, IconComponent, IsNotEmptyPipe, IsNotNullPipe],
  host: { class: 'relative flex border-b border-b-grayborder bg-background-100' },
  template: `
    <button
      [ngClass]="{
        'hover:bg-background-200 flex grow items-start gap-3 px-3 py-2': true,
        'cursor-not-allowed': !downloadAllowed,
      }"
      [disabled]="isLoading || hasMalware"
      [attr.aria-disabled]="isLoading || hasMalware"
      data-test-class="attachment-button"
    >
      <div class="relative shrink">
        @if (fileType && !isLoading && !hasErrors) {
          <ods-file-icon data-test-class="file-icon" [fileType]="fileType" size="large" />
        }
        @if (!isLoading && hasErrors) {
          <ods-file-icon data-test-class="error-file-icon" fileType="exclamation" size="large" />
        }
        @if (isLoading && !hasErrors) {
          <ods-spinner-icon data-test-class="loading-icon" size="extra-large" />
        }
        @if ((badge | isNotNull) && (badge | isNotEmpty) && (badge.name | isNotEmpty)) {
          <ods-icon
            [attr.data-test-class]="'badge-icon-' + badge.name"
            [name]="badge.name"
            [fill]="badge.fill"
            [label]="badge.label"
            size="medium-small"
            class="drop-shadow-malware-icon absolute -right-0.5 -bottom-1"
          />
        }
      </div>
      <div class="text-text flex grow flex-col items-start text-start break-all">
        @if (!hasErrors && !isLoading && caption) {
          <p class="text-sm" data-test-class="caption">
            {{ caption }}
          </p>
        }
        @if (hasErrors && errorCaption) {
          <p class="text-sm" data-test-class="error-caption">
            {{ errorCaption }}
          </p>
        }
        @if (isLoading && loadingCaption) {
          <p class="text-sm" data-test-class="loading-caption">
            {{ loadingCaption }}
          </p>
        }
        @if (description && !hasErrors && !hasMalware) {
          <p class="text-text/65 text-xs" data-test-class="description">
            {{ description }}
          </p>
        }
        @for (error of _errorMessages; track $index) {
          <p class="text-error text-xs" data-test-class="error-item">
            {{ error }}
          </p>
        }
        @for (malwareItem of _malware; track $index) {
          <p class="text-error text-xs" data-test-class="malware-item">
            {{ malwareItem }}
          </p>
        }
      </div>
    </button>
    @if (!isLoading) {
      <ng-content select="[close]" />
    }
  `,
})
export class AttachmentComponent {
  @Input() caption: string = '';
  @Input() loadingCaption: string = '';
  @Input() description: string = '';

  @Input() fileType: FileiconVariants['fileType'] = undefined;
  @Input() isLoading: boolean = false;
  @Input() downloadAllowed: boolean = true;
  @Input() badge: AttachmentBadge;

  @Input() errorCaption: string = '';
  @Input() set errorMessages(errorMessages: string[]) {
    this._errorMessages = errorMessages;
    this.hasErrors = isNotEmpty(errorMessages);
  }
  @Input() set malware(malware: string[]) {
    this._malware = malware;
    this.hasMalware = isNotEmpty(malware);
  }

  public _errorMessages: string[];
  public hasErrors: boolean;

  public _malware: string[];
  public hasMalware: boolean;
}
